/**
 * PII Warning Banner
 *
 * Displayed inline in InputArea when the WASM PII scanner detects
 * sensitive data in the user's input. Shows entity type badges,
 * a masked preview, and action buttons (Mask / Remove / Send Anyway).
 *
 * Color conventions match SecurityPage.tsx PII entity heatmap.
 */

import type { ReactNode } from 'react';
import { Shield, Eye, EyeOff, Send } from 'lucide-react';
import type { PiiFinding } from '../../hooks/usePiiScanner';

interface PiiWarningBannerProps {
  findings: PiiFinding[];
  inputText: string;
  onMask: () => void;
  onRemove: () => void;
  onSendAnyway: () => void;
}

const BADGE_STYLES: Record<string, string> = {
  EMAIL_ADDRESS: 'bg-accent-yellow/15 text-accent-yellow',
  PHONE_NUMBER: 'bg-accent-cyan/15 text-accent-cyan',
  US_SSN: 'bg-accent-red/15 text-accent-red',
  CREDIT_CARD: 'bg-accent-purple/15 text-accent-purple',
  IP_ADDRESS: 'bg-accent-blue/15 text-accent-blue',
  DATE_OF_BIRTH: 'bg-accent-green/15 text-accent-green',
};

const BADGE_LABELS: Record<string, string> = {
  EMAIL_ADDRESS: 'Email',
  PHONE_NUMBER: 'Phone',
  US_SSN: 'SSN',
  CREDIT_CARD: 'Credit Card',
  IP_ADDRESS: 'IP Address',
  DATE_OF_BIRTH: 'Date of Birth',
};

function MaskedPreview({ text, findings }: { text: string; findings: PiiFinding[] }) {
  // Sort findings by start position
  const sorted = [...findings].sort((a, b) => a.start - b.start);

  const parts: ReactNode[] = [];
  let lastEnd = 0;

  sorted.forEach((finding, i) => {
    // Text before this finding
    if (finding.start > lastEnd) {
      parts.push(
        <span key={`text-${i}`} className="text-text-muted">
          {text.slice(lastEnd, finding.start)}
        </span>
      );
    }

    // The PII finding (highlighted)
    const style = BADGE_STYLES[finding.entity_type] || 'bg-accent-orange/15 text-accent-orange';
    parts.push(
      <span
        key={`pii-${i}`}
        className={`${style} px-0.5 rounded-sm font-medium`}
      >
        [{BADGE_LABELS[finding.entity_type] || finding.entity_type}]
      </span>
    );

    lastEnd = finding.end;
  });

  // Text after last finding
  if (lastEnd < text.length) {
    parts.push(
      <span key="text-end" className="text-text-muted">
        {text.slice(lastEnd)}
      </span>
    );
  }

  return (
    <div className="text-[10px] font-mono leading-relaxed truncate">
      {parts}
    </div>
  );
}

export function PiiWarningBanner({
  findings,
  inputText,
  onMask,
  onRemove,
  onSendAnyway,
}: PiiWarningBannerProps) {
  // Deduplicate entity types for badge display
  const uniqueTypes = [...new Set(findings.map((f) => f.entity_type))];

  return (
    <div role="alert" aria-live="polite" className="mt-2 rounded-lg border border-accent-yellow/30 bg-accent-yellow/5 p-2.5">
      {/* Header row: icon + message + badges */}
      <div className="flex items-center gap-2 mb-1.5">
        <Shield className="w-3.5 h-3.5 text-accent-yellow shrink-0" />
        <span className="text-[11px] font-medium text-accent-yellow">
          PII detected ({findings.length})
        </span>
        <div className="flex items-center gap-1 ml-auto">
          {uniqueTypes.map((type) => (
            <span
              key={type}
              className={`px-1.5 py-0.5 rounded-sm text-[9px] font-medium ${BADGE_STYLES[type] || 'bg-bg-hover text-text-muted'}`}
            >
              {BADGE_LABELS[type] || type}
            </span>
          ))}
        </div>
      </div>

      {/* Masked preview */}
      <div className="mb-2 px-2 py-1.5 rounded-sm bg-bg-primary/50 border border-border/50">
        <MaskedPreview text={inputText} findings={findings} />
      </div>

      {/* Action buttons */}
      <div className="flex items-center gap-1.5">
        <button
          type="button"
          onClick={onMask}
          className="flex items-center gap-1 px-2 py-1 rounded-sm text-[10px] font-medium bg-accent-yellow/10 text-accent-yellow border border-accent-yellow/20 hover:bg-accent-yellow/20 transition-colors"
        >
          <EyeOff className="w-2.5 h-2.5" />
          Mask
        </button>
        <button
          type="button"
          onClick={onRemove}
          className="flex items-center gap-1 px-2 py-1 rounded-sm text-[10px] font-medium bg-accent-red/10 text-accent-red border border-accent-red/20 hover:bg-accent-red/20 transition-colors"
        >
          <Eye className="w-2.5 h-2.5" />
          Remove
        </button>
        <button
          type="button"
          onClick={onSendAnyway}
          className="flex items-center gap-1 px-2 py-1 rounded-sm text-[10px] font-medium bg-bg-hover text-text-muted border border-border hover:bg-bg-primary transition-colors ml-auto"
        >
          <Send className="w-2.5 h-2.5" />
          Send Anyway
        </button>
      </div>
    </div>
  );
}
